import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Modal } from "@circle-react-uikit/ModalV2";
import type { Appearance } from "../Store/interfaceSlice";
import type { CssVariables, SiteTheme } from "./constants";
import { useCreateCustomSiteTheme } from "./useCreateCustomSiteTheme";

interface CustomThemeConfirmationModalProps {
  appearance: Appearance;
  cssVariables: CssVariables;
  reset: () => void;
  scope?: "site" | "page";
  toggleCurrentCanvasTheme: (theme: SiteTheme) => void;
}

const CustomThemeConfirmationModalComponent = ({
  appearance,
  cssVariables,
  scope = "page",
  toggleCurrentCanvasTheme,
  reset,
}: CustomThemeConfirmationModalProps) => {
  const modal = useModal();
  const { mutate: createCustomTheme, isLoading: isCreatingCustomTheme } =
    useCreateCustomSiteTheme({ toggleCurrentCanvasTheme, reset });

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {
        void modal.hide();
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title size="sm" className="font-bold">
            {t(
              "builder_ui.theme_configuration.custom_theme_confirmation_modal.title",
            )}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton
              onClick={modal.hide}
              disabled={isCreatingCustomTheme}
            />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="overflow-y-auto pb-11 md:!px-8">
          <Typography.BodyMd color="text-dark">
            {t(
              `builder_ui.theme_configuration.custom_theme_confirmation_modal.${scope}.description`,
            )}
          </Typography.BodyMd>
        </Modal.Body>
        <Modal.Footer className="border-primary flex w-full justify-end gap-4 border-t">
          <Button
            variant="secondary"
            onClick={modal.hide}
            disabled={isCreatingCustomTheme}
          >
            {t(
              "builder_ui.theme_configuration.custom_theme_confirmation_modal.secondary_cta",
            )}
          </Button>
          <Button
            variant="circle"
            disabled={isCreatingCustomTheme}
            onClick={() => {
              createCustomTheme({
                appearance,
                cssVariables,
                themeScope: scope,
              });
            }}
          >
            {t(
              "builder_ui.theme_configuration.custom_theme_confirmation_modal.primary_cta",
            )}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const CustomThemeConfirmationModal = ModalManager.create(
  ({
    appearance,
    cssVariables,
    scope,
    toggleCurrentCanvasTheme,
    reset,
  }: CustomThemeConfirmationModalProps) => (
    <CustomThemeConfirmationModalComponent
      appearance={appearance}
      cssVariables={cssVariables}
      scope={scope}
      toggleCurrentCanvasTheme={toggleCurrentCanvasTheme}
      reset={reset}
    />
  ),
);

export const useCustomThemeConfirmationModal = () =>
  useModal(CustomThemeConfirmationModal);
